/**
 * Language Button Component
 * Mobile-friendly language selector button in top-right corner
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { MapLanguage } from '../types';
import { useTranslation } from '../hooks/useTranslation.tsx';
import styles from './LanguageButton.module.css';

/**
 * Props for Language Button Component
 */
export interface LanguageButtonProps {
  /** Current language */
  language: MapLanguage;
  /** Callback when language changes */
  onLanguageChange: (language: MapLanguage) => void;
}

/**
 * Language Button Component
 * Displays a button in the top-right corner that opens a language selector dropdown
 */
export const LanguageButton: React.FC<LanguageButtonProps> = ({
  language,
  onLanguageChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Supported languages
   */
  const languages: MapLanguage[] = [
    'en',
    'zh-Hans',
    'zh-Hant',
    'es',
    'fr',
    'de',
    'ja',
    'ko',
  ];

  /**
   * Get language display name (full name with English in brackets)
   */
  const getLanguageName = (lang: MapLanguage): string => {
    const name = t?.languages?.[lang];
    return name || lang;
  };

  /**
   * Get language display name without English words (for button display)
   */
  const getLanguageNameShort = (lang: MapLanguage): string => {
    const fullName = t?.languages?.[lang];
    if (!fullName) {
      // Fallback if translation missing
      return lang;
    }
    // Remove English words in brackets, e.g., "简体中文 (Simplified Chinese)" -> "简体中文"
    const match = fullName.match(/^([^(]+)/);
    return match ? match[1].trim() : fullName;
  };

  /**
   * Toggle dropdown
   */
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  /**
   * Handle language selection
   */
  const handleLanguageSelect = useCallback(
    (lang: MapLanguage) => {
      onLanguageChange(lang);
      setIsOpen(false);
    },
    [onLanguageChange]
  );

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  /**
   * Close dropdown on escape key
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className={styles.languageButtonContainer}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={styles.languageButton}
        aria-label={t.controls.language}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className={styles.languageIcon}>🌐</span>
        <span className={styles.languageText}>{getLanguageNameShort(language)}</span>
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div ref={dropdownRef} className={styles.dropdown}>
          {languages.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => handleLanguageSelect(lang)}
              className={`${styles.languageOption} ${
                lang === language ? styles.languageOptionActive : ''
              }`}
              aria-label={getLanguageName(lang)}
            >
              {getLanguageName(lang)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

